// Strategic Data based on actual Eduvos framework
import { StrategicPillar, KeyPerformanceArea, KPI, FunctionalFocusArea, StrategicInitiative } from '../types/kpi';

export const strategicPillars: StrategicPillar[] = [
  {
    id: 'scale',
    name: 'Scale',
    description: 'Grow and optimise existing and new markets, expand product offerings, and align strategic workforce planning',
    color: 'bg-blue-500',
    icon: 'TrendingUp',
    vision: 'Position Eduvos for market growth through new programmes, refreshed portfolios, and international expansion',
    targetYear: 2027,
    keyMetrics: ['enrollment-growth', 'portfolio-refresh', 'market-entry-quals'],
    isActive: true
  },
  {
    id: 'quality',
    name: 'Quality',
    description: 'Academic and operational excellence through curriculum quality, assessment frameworks, and continuous improvement',
    color: 'bg-green-500',
    icon: 'Award',
    vision: 'Deliver exceptional educational experiences and operational excellence',
    targetYear: 2027,
    keyMetrics: ['academic-excellence', 'accreditation-efficiency', 'student-satisfaction'],
    isActive: true
  },
  {
    id: 'culture',
    name: 'Culture',
    description: 'Engagement, leadership development, and values-driven organisational culture',
    color: 'bg-purple-500',
    icon: 'Users',
    vision: 'Foster purpose-driven engagement and exceptional leadership',
    targetYear: 2027,
    keyMetrics: ['employee-engagement', 'leadership-development', 'culture-index'],
    isActive: true
  },
  {
    id: 'impact',
    name: 'Impact',
    description: 'Purpose-driven outcomes, community engagement, and stakeholder value creation',
    color: 'bg-orange-500',
    icon: 'Target',
    vision: 'Create meaningful impact for students, communities, and stakeholders',
    targetYear: 2027,
    keyMetrics: ['graduate-employability', 'community-engagement', 'stakeholder-satisfaction'],
    isActive: true
  }
];

export const functionalFocusAreas: FunctionalFocusArea[] = [
  {
    id: 'academics',
    name: 'Academics',
    description: 'Academic programme delivery, curriculum development, and educational excellence',
    owner: 'Academic Director',
    isActive: true
  },
  {
    id: 'student-affairs',
    name: 'Student Affairs',
    description: 'Student support services, engagement, and experience management',
    owner: 'Student Affairs Director',
    isActive: true
  },
  {
    id: 'sales',
    name: 'Sales',
    description: 'Student recruitment, enrolment, and market development',
    owner: 'Sales Director',
    isActive: true
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Brand management, digital marketing, and market positioning',
    owner: 'Marketing Director',
    isActive: true
  },
  {
    id: 'it',
    name: 'IT',
    description: 'Technology infrastructure, digital transformation, and systems management',
    owner: 'IT Director',
    isActive: true
  },
  {
    id: 'finance-facilities',
    name: 'Finance & Facilities',
    description: 'Financial management, facilities operations, and resource optimisation',
    owner: 'Finance Director',
    isActive: true
  },
  {
    id: 'legal',
    name: 'Legal',
    description: 'Legal compliance, risk management, and regulatory affairs',
    owner: 'Legal Counsel',
    isActive: true
  },
  {
    id: 'hr',
    name: 'HR',
    description: 'Human resources, talent management, and organisational development',
    owner: 'HR Director',
    isActive: true
  },
  {
    id: 'campus-operations',
    name: 'Campus Operations',
    description: 'Campus management, operations, and student services delivery',
    owner: 'Campus Manager',
    isActive: true
  }
];

export const keyPerformanceAreas: KeyPerformanceArea[] = [
  {
    id: 'scale-growth',
    name: 'Scale & Growth',
    description: 'Market expansion, student enrolment growth, and programme portfolio development',
    pillarId: 'scale',
    kpiIds: ['enrollment-growth', 'portfolio-refresh', 'market-entry-quals', 'articulation-agreements', 'student-mobility'],
    functionalAreaIds: ['academics', 'sales', 'marketing'],
    weight: 25,
    isActive: true
  },
  {
    id: 'academic-portfolio-expansion',
    name: 'Academic Portfolio Expansion',
    description: 'New qualifications development, green economy focus, and non-traditional pathways',
    pillarId: 'scale',
    kpiIds: ['new-qualifications', 'green-economy-portfolio', 'micro-credentials', 'accreditation-efficiency'],
    functionalAreaIds: ['academics'],
    weight: 20,
    isActive: true
  },
  {
    id: 'phd-pipeline',
    name: 'PhD Pipeline',
    description: 'Doctoral programme development and postgraduate support structures',
    pillarId: 'scale',
    kpiIds: ['accredited-phds', 'phd-student-support'],
    functionalAreaIds: ['academics'],
    weight: 15,
    isActive: true
  },
  {
    id: 'workforce-agility',
    name: 'Workforce Agility',
    description: 'Strategic workforce planning, automation, and digital literacy',
    pillarId: 'scale',
    kpiIds: ['student-role-ratio', 'automation-mapping', 'digital-literacy', 'staff-adoption'],
    functionalAreaIds: ['hr', 'it'],
    weight: 20,
    isActive: true
  },
  {
    id: 'academic-excellence',
    name: 'Academic Excellence & Curriculum',
    description: 'Curriculum quality, assessment frameworks, and academic standards',
    pillarId: 'quality',
    kpiIds: ['curriculum-quality', 'assessment-effectiveness', 'academic-standards'],
    functionalAreaIds: ['academics'],
    weight: 30,
    isActive: true
  }
];

export const kpis: KPI[] = [
  // Scale & Growth KPIs
  {
    id: 'enrollment-growth',
    name: '# of market-entry qualifications launched',
    description: 'Number of new qualifications launched to enter new markets',
    kpaId: 'scale-growth',
    measurementType: 'count',
    unit: 'qualifications',
    targetValue: 2,
    frequency: 'annually',
    dataSource: 'manual',
    thresholds: {
      excellent: 3,
      good: 2,
      acceptable: 1,
      poor: 0
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'portfolio-refresh',
    name: '% of portfolio refreshed annually',
    description: 'Percentage of academic portfolio refreshed each year',
    kpaId: 'scale-growth',
    measurementType: 'percentage',
    unit: '%',
    targetValue: 20,
    frequency: 'annually',
    dataSource: 'system',
    thresholds: {
      excellent: 25,
      good: 20,
      acceptable: 15,
      poor: 10
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'market-entry-quals',
    name: '% of new programmes meeting enrolment targets within 2 years',
    description: 'Success rate of new programmes in meeting enrolment targets',
    kpaId: 'scale-growth',
    measurementType: 'percentage',
    unit: '%',
    targetValue: 75,
    frequency: 'annually',
    dataSource: 'dynamics_fo',
    thresholds: {
      excellent: 85,
      good: 75,
      acceptable: 65,
      poor: 50
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'articulation-agreements',
    name: '# of articulation/dual-degree agreements',
    description: 'Number of articulation and dual-degree agreements established',
    kpaId: 'scale-growth',
    measurementType: 'count',
    unit: 'agreements',
    targetValue: 2,
    frequency: 'annually',
    dataSource: 'manual',
    thresholds: {
      excellent: 3,
      good: 2,
      acceptable: 1,
      poor: 0
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'student-mobility',
    name: '% students accessing mobility/exchange by 2028',
    description: 'Percentage of students participating in mobility and exchange programmes',
    kpaId: 'scale-growth',
    measurementType: 'percentage',
    unit: '%',
    targetValue: 10,
    frequency: 'annually',
    dataSource: 'system',
    thresholds: {
      excellent: 15,
      good: 10,
      acceptable: 7,
      poor: 5
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  // Academic Portfolio Expansion KPIs
  {
    id: 'new-qualifications',
    name: '# of new qualifications aligned to DHET/SAQA gaps',
    description: 'Number of new qualifications developed to address national skills gaps',
    kpaId: 'academic-portfolio-expansion',
    measurementType: 'count',
    unit: 'qualifications',
    targetValue: 5,
    frequency: 'annually',
    dataSource: 'manual',
    thresholds: {
      excellent: 7,
      good: 5,
      acceptable: 3,
      poor: 1
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'green-economy-portfolio',
    name: '% of portfolio linked to green economy by 2030',
    description: 'Percentage of academic portfolio focused on green economy',
    kpaId: 'academic-portfolio-expansion',
    measurementType: 'percentage',
    unit: '%',
    targetValue: 10,
    frequency: 'annually',
    dataSource: 'system',
    thresholds: {
      excellent: 15,
      good: 10,
      acceptable: 7,
      poor: 5
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'micro-credentials',
    name: '% of students enrolled in micro-credentials/stackables',
    description: 'Percentage of students enrolled in micro-credentials and stackable programmes',
    kpaId: 'academic-portfolio-expansion',
    measurementType: 'percentage',
    unit: '%',
    targetValue: 20,
    frequency: 'quarterly',
    dataSource: 'system',
    thresholds: {
      excellent: 25,
      good: 20,
      acceptable: 15,
      poor: 10
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'accreditation-efficiency',
    name: '% CHE accreditation within 12 months',
    description: 'Percentage of programmes achieving CHE accreditation within 12 months',
    kpaId: 'academic-portfolio-expansion',
    objectiveId: '3',
    measurementType: 'percentage',
    unit: '%',
    targetValue: 90,
    currentValue: 75,
    frequency: 'annually',
    dataSource: 'manual',
    thresholds: {
      excellent: 95,
      good: 90,
      acceptable: 80,
      poor: 70
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  // Additional KPIs for new objectives
  {
    id: 'student-enrollment-rate',
    name: 'Student Enrolment Growth Rate',
    description: 'Year-over-year percentage increase in student enrolment',
    kpaId: 'scale-growth',
    objectiveId: '1',
    initiativeId: 'digital-marketing-campaign',
    measurementType: 'percentage',
    unit: '%',
    targetValue: 15,
    currentValue: 10.2,
    frequency: 'quarterly',
    dataSource: 'dynamics_fo',
    thresholds: {
      excellent: 20,
      good: 15,
      acceptable: 10,
      poor: 5
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  {
    id: 'digital-marketing-roi',
    name: 'Digital Marketing ROI',
    description: 'Return on investment for digital marketing campaigns',
    kpaId: 'scale-growth',
    objectiveId: '1',
    initiativeId: 'digital-marketing-campaign',
    measurementType: 'ratio',
    unit: 'ratio',
    targetValue: 3.5,
    currentValue: 2.8,
    frequency: 'monthly',
    dataSource: 'system',
    thresholds: {
      excellent: 4.0,
      good: 3.5,
      acceptable: 2.5,
      poor: 1.5
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  {
    id: 'green-economy-programs',
    name: 'Green Economy Programs Launched',
    description: 'Number of new green economy focused academic programs',
    kpaId: 'academic-portfolio-expansion',
    objectiveId: '3',
    initiativeId: 'green-economy-curriculum',
    measurementType: 'count',
    unit: 'programs',
    targetValue: 3,
    currentValue: 1,
    frequency: 'quarterly',
    dataSource: 'manual',
    thresholds: {
      excellent: 4,
      good: 3,
      acceptable: 2,
      poor: 1
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  {
    id: 'curriculum-quality-score',
    name: 'Curriculum Quality Assessment Score',
    description: 'Average quality score from curriculum assessments',
    kpaId: 'academic-excellence',
    objectiveId: '4',
    initiativeId: 'curriculum-excellence-framework',
    measurementType: 'score',
    unit: 'score',
    targetValue: 85,
    currentValue: 78,
    frequency: 'annually',
    dataSource: 'system',
    thresholds: {
      excellent: 90,
      good: 85,
      acceptable: 75,
      poor: 65
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  {
    id: 'leadership-program-completion',
    name: 'Leadership Program Completion Rate',
    description: 'Percentage of staff completing leadership development programs',
    kpaId: 'workforce-agility',
    objectiveId: '5',
    initiativeId: 'leadership-development-program',
    measurementType: 'percentage',
    unit: '%',
    targetValue: 80,
    currentValue: 65,
    frequency: 'quarterly',
    dataSource: 'system',
    thresholds: {
      excellent: 90,
      good: 80,
      acceptable: 70,
      poor: 50
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  {
    id: 'community-partnerships',
    name: 'Active Community Partnerships',
    description: 'Number of active partnerships with community organisations',
    kpaId: 'scale-growth',
    objectiveId: '6',
    initiativeId: 'community-engagement-initiative',
    measurementType: 'count',
    unit: 'partnerships',
    targetValue: 12,
    currentValue: 8,
    frequency: 'quarterly',
    dataSource: 'manual',
    thresholds: {
      excellent: 15,
      good: 12,
      acceptable: 10,
      poor: 6
    },
    trend: 'up',
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  }
];

export const strategicInitiatives: StrategicInitiative[] = [
  // Scale Pillar Initiatives
  {
    id: 'digital-marketing-campaign',
    title: 'Digital Marketing Campaign',
    description: 'Comprehensive social media and online advertising strategy to increase student enrolment',
    objectiveId: '1', // Links to "Increase student enrolment by 15%"
    pillarId: 'scale',
    code: 'SCALE-01',
    startDate: '2024-01-01',
    endDate: '2024-06-30',
    budget: 250000,
    functionalAreas: ['marketing'],
    kpaId: 'scale-growth',
    kpiIds: ['enrollment-growth', 'portfolio-refresh'],
    operationalInitiatives: [
      'Social media advertising campaigns',
      'Google Ads optimisation',
      'Content marketing strategy',
      'Influencer partnerships',
      'Lead generation automation'
    ],
    status: 'active',
    priority: 'high',
    owner: 'Digital Marketing Team',
    progress: 75,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  {
    id: 'campus-outreach-program',
    title: 'Campus Outreach Program',
    description: 'High school visits and community engagement to attract prospective students',
    objectiveId: '1', // Links to "Increase student enrolment by 15%"
    pillarId: 'scale',
    code: 'SCALE-02',
    startDate: '2024-02-01',
    endDate: '2024-11-30',
    budget: 150000,
    functionalAreas: ['sales', 'marketing'],
    kpaId: 'scale-growth',
    kpiIds: ['enrollment-growth', 'market-entry-quals'],
    operationalInitiatives: [
      'High school career days',
      'Community college partnerships',
      'Open house events',
      'Campus tours program',
      'Student ambassador program'
    ],
    status: 'active',
    priority: 'medium',
    owner: 'Sales Team',
    progress: 60,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  {
    id: 'green-economy-curriculum',
    title: 'Green Economy Curriculum Development',
    description: 'Create new qualifications focused on sustainability and green economy skills',
    objectiveId: '3', // Links to "Develop green economy qualifications"
    pillarId: 'scale',
    code: 'SCALE-03',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    budget: 500000,
    functionalAreas: ['academics'],
    kpaId: 'academic-portfolio-expansion',
    kpiIds: ['new-qualifications', 'green-economy-portfolio'],
    operationalInitiatives: [
      'Renewable energy programmes',
      'Sustainable business practices',
      'Environmental management qualifications',
      'Green technology certifications',
      'Industry advisory board establishment'
    ],
    status: 'active',
    priority: 'high',
    owner: 'Academic Affairs',
    progress: 35,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  // Quality Pillar Initiatives
  {
    id: 'curriculum-excellence-framework',
    title: 'Curriculum Excellence Framework',
    description: 'Implement comprehensive curriculum quality assurance and continuous improvement processes',
    objectiveId: '4', // New objective for Quality pillar
    pillarId: 'quality',
    code: 'QUAL-01',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    budget: 300000,
    functionalAreas: ['academics'],
    kpaId: 'academic-excellence',
    kpiIds: ['accreditation-efficiency'],
    operationalInitiatives: [
      'Curriculum mapping and alignment',
      'Assessment standardization',
      'Faculty development programs',
      'Student feedback integration',
      'Industry relevance reviews'
    ],
    status: 'active',
    priority: 'high',
    owner: 'Academic Quality Team',
    progress: 45,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  // Culture Pillar Initiatives
  {
    id: 'leadership-development-program',
    title: 'Leadership Development Program',
    description: 'Comprehensive leadership development for staff at all levels',
    objectiveId: '5', // New objective for Culture pillar
    pillarId: 'culture',
    code: 'CULT-01',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    budget: 200000,
    functionalAreas: ['hr'],
    kpaId: 'workforce-agility',
    kpiIds: ['digital-literacy', 'staff-adoption'],
    operationalInitiatives: [
      'Management training workshops',
      'Mentorship programs',
      'Leadership assessments',
      'Cross-functional projects',
      'Executive coaching'
    ],
    status: 'active',
    priority: 'medium',
    owner: 'HR Development Team',
    progress: 55,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  },
  // Impact Pillar Initiatives
  {
    id: 'community-engagement-initiative',
    title: 'Community Engagement Initiative',
    description: 'Strengthen partnerships with local communities and industry stakeholders',
    objectiveId: '6', // New objective for Impact pillar
    pillarId: 'impact',
    code: 'IMPT-01',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    budget: 180000,
    functionalAreas: ['student-affairs', 'academics'],
    kpaId: 'scale-growth', // Temporary - need Impact KPAs
    kpiIds: ['articulation-agreements'],
    operationalInitiatives: [
      'Community service programs',
      'Industry partnership development',
      'Alumni engagement network',
      'Local business collaborations',
      'Skills development workshops'
    ],
    status: 'active',
    priority: 'medium',
    owner: 'Community Relations Team',
    progress: 40,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-03-01T00:00:00Z'
  }
];
